// MultiStepForm.js
import React, { useEffect, useState } from "react";
import { useRef } from "react";
import {
  Container,
  Form,
  Button,
  ProgressBar,
  Nav,
  Alert,
} from "react-bootstrap";
import SignaturePad from "react-signature-canvas"; // Import the SignaturePad component
import "./Signature.css";
import { getSasToken } from "../../store/thunk/user";
import { connect } from "react-redux";
import { updateForm } from "../../utils/updateForm";
import { toast } from "react-toastify";
import { triggerButtonClick } from "../../utils/triggerLibraryButton";
import { uploadDocumentAndGetUrl } from "../../utils/uploadDocument";
import { dataURLtoBlob } from "../../utils/dataURLtoBlob";
import NewStarterChecklistDownload from "./Download/NewStarterChecklistDownload";
import { Tabs, Tab, Box } from "@mui/material";
import moment from "moment";

const NewStarterChecklist = ({
  currentDocument,
  documentList,
  user,
  sasToken,
  ipAddress,
  getSasToken,
}) => {
  const [step, setStep] = useState(1);
  const [value, setValue] = useState(0);
  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };
  const [progress, setProgress] = useState(0); // Progress percentage for each step
  const [formData, setFormData] = useState({
    industrialOrDrivingWork: [],
    safetyBootOrH1: "",
    title: "",
    firstName: "",
    surName: "",
    homeAddress: "",
    postCode: "",
    mobile: "",
    landlineNumber: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    nationalInsuranceNumber: "",
    criminalConviction: "",
    criminalConvictionDetail: "",
    holdUkDrivingLicense: "",
    sameDayToReportWork: "",
    employeeStatement: "",
    haveStudentLoan: "",
    typeOfLoan: "",
    finishStudies: "",
    kinName: "",
    kinNumber: "",
    kinRelation: "",
    consent: false,
    signature: "", // Store the signature data as a string
  });
  const [errors, setErrors] = useState({});
  const sigPadRef = useRef(null); // Reference to the SignaturePad component
  useEffect(() => {
    setFormData(currentDocument);
    if (currentDocument?.step) {
      setStep(currentDocument?.step);
    }
    if (currentDocument?.progress) {
      setProgress(currentDocument?.progress);
    }
    if (currentDocument?.progress === 100) {
      getSasToken();
    }
  }, []);
  // Function to handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    setErrors({ ...errors, [name]: "" });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === "Industrial" || name === "Driving") {
      setFormData((prevData) => {
        const industrialOrDrivingWork = Array.isArray(
          prevData.industrialOrDrivingWork
        )
          ? prevData.industrialOrDrivingWork
          : []; // Ensure it's an array

        const updatedOptions = checked
          ? [...industrialOrDrivingWork, name] // Add option if checked
          : industrialOrDrivingWork.filter((option) => option !== name); // Remove option if unchecked

        return {
          ...prevData,
          industrialOrDrivingWork: updatedOptions,
        };
      });
    } else {
      setFormData((prevData) => {
        const safetyBootOrH1 = Array.isArray(prevData.safetyBootOrH1)
          ? prevData.safetyBootOrH1
          : []; // Ensure it's an array

        const updatedOptions = checked
          ? [...safetyBootOrH1, name] // Add option if checked
          : safetyBootOrH1.filter((option) => option !== name); // Remove option if unchecked

        return {
          ...prevData,
          safetyBootOrH1: updatedOptions,
        };
      });
    }
  };

  // Function to validate each step
  const validateStep = () => {
    let formErrors = {};

    // Default values to avoid undefined issues
    const industrialOrDrivingWork = Array.isArray(
      formData.industrialOrDrivingWork
    )
      ? formData.industrialOrDrivingWork
      : [];

    if (step === 1) {
      // Validate checkbox group
      if (industrialOrDrivingWork.length === 0) {
        formErrors.industrialOrDrivingWork =
          "Please select at least one option";
      }
      if (!formData.title) formErrors.title = "Title is required";
      if (!formData.criminalConviction)
        formErrors.criminalConviction = "This is a required field";
      if (!formData.holdUkDrivingLicense)
        formErrors.holdUkDrivingLicense = "This is a required field";
      if (!formData.sameDayToReportWork)
        formErrors.sameDayToReportWork = "This is a required field";
      if (!formData.safetyBootOrH1)
        formErrors.safetyBootOrH1 = "This is a required field";
      if (!formData.firstName) formErrors.firstName = "First Name is required";
      if (!formData.surName) formErrors.surName = "Surname is required";
      if (!formData.homeAddress)
        formErrors.homeAddress = "Home address is required";
      if (!formData.postCode) formErrors.postCode = "Post code is required";
      if (!formData.mobile) formErrors.mobile = "Mobile number is required";
      if (!/^\d{11}$/.test(formData.mobile)) {
        formErrors.mobile =
          "Mobile number must be exactly 11 digits and contain only numbers";
      }
      if (!formData.email) {
        formErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        formErrors.email = "Email is invalid";
      }
    } else if (step === 2) {
      // Validation for step 2
    } else if (step === 3) {
      if (!formData.haveStudentLoan)
        formErrors.haveStudentLoan = "This field is required";

      if (formData.haveStudentLoan === "Yes") {
        if (!formData.typeOfLoan)
          formErrors.typeOfLoan = "This field is required";
        if (!formData.finishStudies)
          formErrors.finishStudies = "This field is required";
      }
      // Validation for step 3
    } else if (step === 4) {
      if (!formData.kinName) formErrors.kinName = "Name is a required field";
      if (!formData.kinNumber)
        formErrors.kinNumber = "Number is a required field";
      if (!formData.kinRelation)
        formErrors.kinRelation = "Relation is a required field";
    } else if (step === 5) {
      if (!formData.consent) formErrors.consent = "You must agree to the terms";
      if (sigPadRef.current.isEmpty())
        formErrors.signature = "Signature is required"; // Check if the signature pad is empty
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Function to go to the next step
  const nextStep = () => {
    if (validateStep()) {
      const payload = {
        [currentDocument["key"]]: {
          name: currentDocument?.name,
          ...formData,
          progress: progress + 20,
          step: step + 1,
        },
      };
      updateForm(payload, documentList, user);
      setStep(step + 1);
      setProgress(progress + 20);
    }
  };

  // Function to go to the previous step
  const prevStep = () => {
    setStep(step - 1);
    setProgress(progress - 20);
  };

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep()) {
      // Get the signature data as a base64 image
      const signatureImage = sigPadRef.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
      const signatureBlob = dataURLtoBlob(signatureImage);
      setFormData({ ...formData, signature: signatureImage });
      // Create a FormData object
      const formDataSign = new FormData();
      // Append the Blob as a file to the FormData
      // The second argument is the file name, which you can customize as needed
      formDataSign.append(
        "file",
        signatureBlob,
        `${[currentDocument["key"]]}.png`
      );
      // Send the FormData using postMultiPartFormData
      const documentUrl =
        (await uploadDocumentAndGetUrl(formDataSign, user?.token)) || "";
      if (documentUrl) {
        const payload = {
          [currentDocument["key"]]: {
            name: currentDocument?.name,
            ...formData,
            progress: 100,
            step: step,
            signature: documentUrl,
            signDate: new Date(),
            ipAddress: ipAddress,
          },
        };
        updateForm(payload, documentList, user);
        toast.success("Successfully filled the form.");
        triggerButtonClick();
      } else {
        toast.error(
          "Something went wrong while upload signature. please try again."
        );
      }
      // Additional submit logic here, such as sending formData to the server
    }
  };
  // Clear the signature pad
  const clearSignature = () => {
    sigPadRef.current.clear();
    setFormData({ ...formData, signature: "" });
  };
  return (
    <Container className="mt-5">
      {currentDocument?.progress === 100 ? (
        <>
          <NewStarterChecklistDownload
            data={currentDocument}
            sasToken={sasToken}
            user={documentList?.userDetail}
          />
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              aria-label="basic tabs example"
              className="tab mt-2"
            >
              <Tab label="Personal Information" />
              <Tab label="Employment Statement" />
              <Tab label="Student Loan" />
              <Tab label="Next of kin" />
            </Tabs>
            {value === 0 && (
              <div className="container">
                <div className="row">
                  <h2>Personal Information</h2>
                  <ul class="list-group p-0">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Industrial or Driving Work Wanted?</strong>{" "}
                      <span>{currentDocument?.industrialOrDrivingWork ? String(currentDocument?.industrialOrDrivingWork) : "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Title</strong>{" "}
                      <span>{currentDocument?.title || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>First Name</strong>{" "}
                      <span>{currentDocument?.firstName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Surname</strong>{" "}
                      <span>{currentDocument?.surName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Home Address</strong>{" "}
                      <span>{currentDocument?.homeAddress || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Postcode</strong>{" "}
                      <span>{currentDocument?.postCode || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Mobile Number</strong>{" "}
                      <span>{currentDocument?.mobile || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Landline Number</strong>{" "}
                      <span>{currentDocument?.landlineNumber || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Email Address</strong>{" "}
                      <span>{currentDocument?.email || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>DOB</strong>{" "}
                      <span>{currentDocument?.dateOfBirth || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Are you male or female?</strong>{" "}
                      <span>{currentDocument?.gender || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>National Insurance Number</strong>{" "}
                      <span>{currentDocument?.nationalInsuranceNumber || "--"}</span>
                    </li>
                    {/* <li class="list-group-item d-flex justify-content-between">
                      <strong>Employment start date</strong>{" "}
                      <span>{currentDocument?.sortCode || "--"}</span>
                    </li> */}
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Criminal Convictions</strong>{" "}
                      <span>{currentDocument?.criminalConviction || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Please provide details of any criminal convictions, except any chich are "spent" in accordance with the Rehabilitation pf offenders act 1974. If none please state none</strong>{" "}
                      <span>{currentDocument?.criminalConvictionDetail || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Do you hold a valid UK driving license (and Drive to work)</strong>{" "}
                      <span>{currentDocument?.holdUkDrivingLicense || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Can you report to work the same day we call you?</strong>{" "}
                      <span>{currentDocument?.sameDayToReportWork || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Do you have Safety Boots and Hi-Vis?</strong>{" "}
                      <span>{currentDocument?.safetyBootOrH1 || "--"}</span>
                    </li>
                    {/* <li class="list-group-item d-flex justify-content-between">
                      <strong>Sharecode</strong>{" "}
                      <span>{currentDocument?.sortCode || "--"}</span>
                    </li> */}
                  </ul>
                </div>
              </div>
            )}
            {value === 1 && (
              <div className="container">
                <div className="row">
                  <h2>Employee Statement</h2>
                  <ul class="list-group p-0">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>
                        1. You need to select only one of the following
                        statements A, B or C
                      </strong>{" "}
                    </li>
                    <li className="list-group-item ">
                      {currentDocument?.employeeStatement === "A" && (
                        <p>
                          A. This is my first job since last 6 April and I have
                          not been receiving taxable Jobseeker’s Allowance,
                          Employment and Support Allowance, taxable Incapacity
                          Benefit, State or Occupational Pension.
                        </p>
                      )}
                      {currentDocument?.employeeStatement === "B" && (
                        <p>
                          B. This is now my only job but since last 6 April I
                          have had another job, or received taxable Jobseeker’s
                          Allowance, Employment and Support Allowance or taxable
                          Incapacity Benefit. I do not receive a State or
                          Occupational Pension.
                        </p>
                      )}
                      {currentDocument?.employeeStatement === "C" && (
                        <p>
                          C. As well as my new job, I have another job or
                          receive a State or Occupational Pension.
                        </p>
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            )}
            {value === 2 && (
              <div className="container">
                <div className="row">
                  <h2>Student Loan</h2>
                  <ul class="list-group p-0">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>2. Do you have a Student Loan which is not fully repaid?</strong>{" "}
                      <span>{currentDocument?.haveStudentLoan || "--"}</span>
                    </li>

                    <li class="list-group-item d-flex justify-content-between">
                      <strong>3. What type of Student Loan do you have?</strong>{" "}
                      <span>{currentDocument?.typeOfLoan || "--"}</span>
                    </li>

                    <li class="list-group-item d-flex justify-content-between">
                      <strong>4. Did you finish your studies before the last 6 April?</strong>{" "}
                      <span>{currentDocument?.finishStudies || "--"}</span>
                    </li>
                    
                  </ul>
                </div>
              </div>
            )}
            {value === 3 && (
              <div className="container">
                <div className="row">
                  <h2>Next of kin</h2>
                  <ul class="list-group p-0">
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Name</strong>{" "}
                      <span>{currentDocument?.kinName || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Number</strong>{" "}
                      <span>{currentDocument?.kinNumber || "--"}</span>
                    </li>
                    <li class="list-group-item d-flex justify-content-between">
                      <strong>Relationship</strong>{" "}
                      <span>{currentDocument?.kinRelation || "--"}</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
            <ul className="list-group mt-2 p-0">
              <li className="list-group-item d-flex">
                <div className="row">
                  <div className="col-12">
                    {currentDocument?.signature && (
                      <img
                        width={100}
                        src={`${currentDocument?.signature}?${sasToken}`}
                        alt="Signature"
                      />
                    )}
                  </div>
                </div>
              </li>
              <li className="list-group-item d-flex">
                Sign Date:{" "}
                {moment(currentDocument?.signDate).format("DD-MM-YYYY")}
              </li>
            </ul>
          </Box>
        </>
      ) : (
        <>
          <Nav variant="pills" className="justify-content-center mb-3">
            <Nav.Item>
              <Nav.Link
                className={step === 1 ? "text-light" : ""}
                active={step === 1}
              >
                Step 1
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 2 ? "text-light" : ""}
                active={step === 2}
              >
                Step 2
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 3 ? "text-light" : ""}
                active={step === 3}
              >
                Step 3
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 4 ? "text-light" : ""}
                active={step === 4}
              >
                Step 4
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                className={step === 5 ? "text-light" : ""}
                active={step === 5}
              >
                Step 5
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <ProgressBar now={progress} className="mb-4" />

          <Form onSubmit={handleSubmit}>
            {step === 1 && (
              <div>
                <h3>Section 1: Personal Information</h3>
                <Form.Group className="mb-3">
                  <Form.Label>Industrial or Driving Work Wanted?</Form.Label>
                  <Form.Check
                    type="checkbox"
                    name="Industrial"
                    label="Industrial"
                    checked={formData?.industrialOrDrivingWork?.includes(
                      "Industrial"
                    )}
                    onChange={handleCheckboxChange}
                  />
                  <Form.Check
                    type="checkbox"
                    name="Driving"
                    label="Driving"
                    checked={formData?.industrialOrDrivingWork?.includes(
                      "Driving"
                    )}
                    onChange={handleCheckboxChange}
                  />
                  {errors.industrialOrDrivingWork && (
                    <Alert variant="danger">
                      {errors.industrialOrDrivingWork}
                    </Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    required
                  />
                  {errors.title && (
                    <Alert variant="danger">{errors.title}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    required
                  />
                  {errors.firstName && (
                    <Alert variant="danger">{errors.firstName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Surname</Form.Label>
                  <Form.Control
                    type="text"
                    name="surName"
                    value={formData.surName}
                    onChange={handleChange}
                    isInvalid={!!errors.surName}
                    required
                  />
                  {errors.surName && (
                    <Alert variant="danger">{errors.surName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Home Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="homeAddress"
                    value={formData.homeAddress}
                    onChange={handleChange}
                    isInvalid={!!errors.homeAddress}
                    required
                  />
                  {errors.homeAddress && (
                    <Alert variant="danger">{errors.homeAddress}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Postcode</Form.Label>
                  <Form.Control
                    type="text"
                    name="postCode"
                    value={formData.postCode}
                    onChange={handleChange}
                    isInvalid={!!errors.postCode}
                    required
                  />
                  {errors.postCode && (
                    <Alert variant="danger">{errors.postCode}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    isInvalid={!!errors.mobile}
                    required
                  />
                  {errors.mobile && (
                    <Alert variant="danger">{errors.mobile}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Landline Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="landlineNumber"
                    value={formData.landlineNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    required
                  />
                  {errors.email && (
                    <Alert variant="danger">{errors.email}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>DOB</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Are you male or female?</Form.Label>
                  <Form.Check
                    type="radio"
                    name="gender"
                    label="Male"
                    value="Male"
                    checked={formData.gender === "Male"}
                    onChange={handleChange}
                  />
                  <Form.Check
                    type="radio"
                    name="gender"
                    label="Female"
                    value="Female"
                    checked={formData.gender === "Female"}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>National Insurance Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="nationalInsuranceNumber"
                    value={formData.nationalInsuranceNumber}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Criminal Convictions</Form.Label>
                  <Form.Check
                    type="radio"
                    name="criminalConviction"
                    label="Yes"
                    value="Yes"
                    checked={formData.criminalConviction === "Yes"}
                    onChange={handleChange}
                    isInvalid={!!errors.criminalConviction}
                  />
                  <Form.Check
                    type="radio"
                    name="criminalConviction"
                    label="No"
                    value="No"
                    checked={formData.criminalConviction === "No"}
                    onChange={handleChange}
                    isInvalid={!!errors.criminalConviction}
                  />
                  {errors.criminalConviction && (
                    <Alert variant="danger">{errors.criminalConviction}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Please provide details of any criminal convictions, except any chich are "spent" in accordance with the Rehabilitation pf offenders act 1974. If none please state none
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="criminalConvictionDetail"
                    value={formData.criminalConvictionDetail}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Do you hold a valid UK driving license (and Drive to work)
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    name="holdUkDrivingLicense"
                    label="Yes"
                    value="Yes"
                    checked={formData.holdUkDrivingLicense === "Yes"}
                    onChange={handleChange}
                    isInvalid={!!errors.holdUkDrivingLicense}
                  />
                  <Form.Check
                    type="radio"
                    name="holdUkDrivingLicense"
                    label="No"
                    value="No"
                    checked={formData.holdUkDrivingLicense === "No"}
                    onChange={handleChange}
                    isInvalid={!!errors.holdUkDrivingLicense}
                  />
                  {errors.holdUkDrivingLicense && (
                    <Alert variant="danger">
                      {errors.holdUkDrivingLicense}
                    </Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Can you report to work the same day we call you?
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    name="sameDayToReportWork"
                    label="Yes"
                    value="Yes"
                    checked={formData.sameDayToReportWork === "Yes"}
                    onChange={handleChange}
                    isInvalid={!!errors.sameDayToReportWork}
                  />
                  <Form.Check
                    type="radio"
                    name="sameDayToReportWork"
                    label="No"
                    value="No"
                    checked={formData.sameDayToReportWork === "No"}
                    onChange={handleChange}
                    isInvalid={!!errors.sameDayToReportWork}
                  />
                  {errors.sameDayToReportWork && (
                    <Alert variant="danger">{errors.sameDayToReportWork}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Do you have Safety Boots and Hi-Vis?</Form.Label>
                  <Form.Check
                    type="radio"
                    name="safetyBootOrH1"
                    label="Yes"
                    value="Yes"
                    checked={formData.safetyBootOrH1 === "Yes"}
                    onChange={handleChange}
                    isInvalid={!!errors.safetyBootOrH1}
                  />
                  <Form.Check
                    type="radio"
                    name="safetyBootOrH1"
                    label="No"
                    value="No"
                    checked={formData.safetyBootOrH1 === "No"}
                    onChange={handleChange}
                    isInvalid={!!errors.safetyBootOrH1}
                  />
                  {errors.safetyBootOrH1 && (
                    <Alert variant="danger">{errors.safetyBootOrH1}</Alert>
                  )}
                </Form.Group>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3>Step 2: Employee Statement</h3>

                <Form.Group className="mb-3">
                  <Form.Label>
                    1. You need to select only one of the following statements
                    A, B or C
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    name="employeeStatement"
                    label="A. This is my first job since last 6 April and I have not been receiving taxable Jobseeker’s Allowance, Employment and Support Allowance, taxable Incapacity Benefit, State or Occupational Pension."
                    value="A"
                    checked={formData.employeeStatement === "A"}
                    onChange={handleChange}
                  />
                  <Form.Check
                    type="radio"
                    name="employeeStatement"
                    label="B. This is now my only job but since last 6 April I have had another job, or received taxable Jobseeker’s Allowance, Employment and Support Allowance or taxable Incapacity Benefit. I do not receive a State or Occupational Pension."
                    value="B"
                    checked={formData.employeeStatement === "B"}
                    onChange={handleChange}
                  />
                  <Form.Check
                    type="radio"
                    name="employeeStatement"
                    label="C.As well as my new job, I have another job or receive a State or Occupational Pension."
                    value="C"
                    checked={formData.employeeStatement === "C"}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3>Step 3: Student Loan</h3>

                <Form.Group className="mb-3">
                  <Form.Label>
                    2. Do you have a Student Loan which is not fully repaid?
                  </Form.Label>
                  <Form.Check
                    type="radio"
                    name="haveStudentLoan"
                    label="Yes"
                    value="Yes"
                    checked={formData.haveStudentLoan === "Yes"}
                    onChange={handleChange}
                    isInvalid={!!errors.haveStudentLoan}
                  />
                  <Form.Check
                    type="radio"
                    name="haveStudentLoan"
                    label="No"
                    value="No"
                    checked={formData.haveStudentLoan === "No"}
                    onChange={handleChange}
                    isInvalid={!!errors.haveStudentLoan}
                  />
                  {errors.haveStudentLoan && (
                    <Alert variant="danger">{errors.haveStudentLoan}</Alert>
                  )}
                </Form.Group>
                {formData.haveStudentLoan === "Yes" && (
                  <>
                    <h5 className="mt-4">Student Loan Plans</h5>
                    <div className="mt-2">
                      <p>
                        <strong>You will have a Plan 1 Student Loan if:</strong>
                      </p>
                      <ul>
                        <li>
                          You lived in Scotland or Northern Ireland when you
                          started your course, or
                        </li>
                        <li>
                          You lived in England or Wales and started your course
                          before September 2012
                        </li>
                      </ul>

                      <p>
                        <strong>You will have a Plan 2 Student Loan if:</strong>
                      </p>
                      <ul>
                        <li>
                          You lived in England or Wales and started your course
                          on or after 1 September 2012.
                        </li>
                      </ul>
                    </div>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        3. What type of Student Loan do you have?
                      </Form.Label>
                      <Form.Check
                        type="radio"
                        name="typeOfLoan"
                        label="Plan 1"
                        value="Plan1"
                        checked={formData.typeOfLoan === "Plan1"}
                        onChange={handleChange}
                        isInvalid={!!errors.typeOfLoan}
                      />
                      <Form.Check
                        type="radio"
                        name="typeOfLoan"
                        label="Plan 2"
                        value="Plan2"
                        checked={formData.typeOfLoan === "Plan2"}
                        onChange={handleChange}
                        isInvalid={!!errors.typeOfLoan}
                      />
                      {errors.typeOfLoan && (
                        <Alert variant="danger">{errors.typeOfLoan}</Alert>
                      )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        4. Did you finish your studies before the last 6 April?
                      </Form.Label>
                      <Form.Check
                        type="radio"
                        name="finishStudies"
                        label="Yes"
                        value="Yes"
                        checked={formData.finishStudies === "Yes"}
                        onChange={handleChange}
                        isInvalid={!!errors.finishStudies}
                      />
                      <Form.Check
                        type="radio"
                        name="finishStudies"
                        label="No"
                        value="No"
                        checked={formData.finishStudies === "No"}
                        onChange={handleChange}
                        isInvalid={!!errors.finishStudies}
                      />
                      {errors.finishStudies && (
                        <Alert variant="danger">{errors.finishStudies}</Alert>
                      )}
                    </Form.Group>
                  </>
                )}

                <p className="mt-2">
                  For further guidance about repaying Student Loans go to
                  www.gov.uk/new-employee/student-loans
                </p>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}
            {step === 4 && (
              <div>
                <h3>Step 4: Next Of Kin</h3>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="kinName"
                    value={formData.kinName}
                    onChange={handleChange}
                    isInvalid={!!errors.kinName}
                    required
                  />
                  {errors.kinName && (
                    <Alert variant="danger">{errors.kinName}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Telephone Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="kinNumber"
                    value={formData.kinNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.kinNumber}
                    required
                  />
                  {errors.kinNumber && (
                    <Alert variant="danger">{errors.kinNumber}</Alert>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Relation</Form.Label>
                  <Form.Control
                    type="text"
                    name="kinRelation"
                    value={formData.kinRelation}
                    onChange={handleChange}
                    isInvalid={!!errors.kinRelation}
                    required
                  />
                  {errors.kinRelation && (
                    <Alert variant="danger">{errors.kinRelation}</Alert>
                  )}
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="primary" onClick={nextStep}>
                  Next
                </Button>
              </div>
            )}
            {step === 5 && (
              <div>
                <h3>Step 5: Sign and Submit</h3>
                <p>
                  By signing this page, I confirm that I fully understand the
                  content and agree to adhere to it at all times. I also agree
                  that all the details provided in this document are accurate
                  and complete to the best of my knowledge.
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Signature</Form.Label>
                  <div className="signature-pad">
                    <SignaturePad
                      ref={sigPadRef}
                      penColor="black"
                      canvasProps={{ className: "signature-canvas" }} // Customize the canvas appearance
                    />
                  </div>
                  {errors.signature && (
                    <Alert variant="danger">{errors.signature}</Alert>
                  )}
                  <Button
                    variant="secondary"
                    onClick={clearSignature}
                    className="mt-2"
                  >
                    Clear Signature
                  </Button>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="consent"
                    label="Clear By ticking this box I consent to the company processing my
                data and the evidence of this document being stored within their
                system for the purpose of work finding services."
                    checked={formData.consent} // Use checked for checkboxes
                    onChange={handleChange}
                    isInvalid={!!errors.consent}
                  />
                  {errors.consent && (
                    <Alert variant="danger">{errors.consent}</Alert>
                  )}
                </Form.Group>
                <Button variant="secondary" onClick={prevStep} className="me-2">
                  Previous
                </Button>
                <Button variant="success" type="submit">
                  Submit
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  currentDocument: state.user.currentDocument,
  documentList: state.user.documentList,
  user: state.user.loggedInUserData,
  sasToken: state.user.sasToken,
  ipAddress: state.user.ipAddress,
});
export default connect(mapStateToProps, { getSasToken })(NewStarterChecklist);
